import { DIMENSOES_AGRUPAMENTO, type DimensaoAgrupamento } from '@baiocchi/shared'
import {
  Alerta,
  BarrasHorizontais,
  CabecalhoCartao,
  Campo,
  Cartao,
  CartaoIndicador,
  CorpoCartao,
  Esqueleto,
  EstadoVazio,
  Etiqueta,
  Selecao,
} from '@baiocchi/ui'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { chaves } from '../../api/chaves'
import { conversoesApi } from '../../api/indicadores'
import { EstadoDeErro } from '../../componentes/estados-tela'
import { FiltroPeriodo } from '../../componentes/filtro-periodo'
import { urlDoDrilldown } from '../../dominio/drilldown'
import { useFiltrosUrl } from '../../ganchos/usar-filtros-url'
import { CabecalhoPagina } from '../../layout/layout-app'
import { formatarNumero, formatarPercentual } from '../../utilitarios/formatar'

const ROTULO_DIMENSAO: Record<DimensaoAgrupamento, string> = {
  pontoConversao: 'Ponto de conversão',
  equipe: 'Equipe',
  unidade: 'Unidade',
  responsavel: 'Responsável',
  motivo: 'Motivo de perda',
}

/**
 * Relatório de perdas (`GET /conversoes/perdas`).
 *
 * O **cruzamento** motivo × dimensão existe porque a resposta útil quase nunca é o
 * motivo isolado, e sim "Contato Inválido concentrado em um ponto de conversão" — que
 * é uma decisão de mídia, não de atendimento. Com `agruparPor=motivo` o cruzamento sai
 * vazio: cruzar motivo com motivo não diz nada.
 */
export function PaginaPerdas() {
  const { filtros, definir } = useFiltrosUrl({ agruparPor: 'pontoConversao' })
  const navegar = useNavigate()

  const consultaFiltros = {
    de: filtros.de,
    ate: filtros.ate,
    unidadeId: filtros.unidadeId,
    equipeId: filtros.equipeId,
    agruparPor: filtros.agruparPor,
  }

  const consulta = useQuery({
    queryKey: chaves.indicadores.perdas(consultaFiltros),
    queryFn: ({ signal }) => conversoesApi.perdas(consultaFiltros, signal),
    staleTime: 60_000,
  })

  const dados = consulta.data?.dados
  const dimensao = (filtros.agruparPor as DimensaoAgrupamento) ?? 'pontoConversao'

  return (
    <>
      <CabecalhoPagina
        titulo="Perdas por motivo"
        descricao="Por que estamos perdendo, e onde a perda se concentra."
      />

      <Cartao className="mb-5">
        <div className="flex flex-wrap items-end gap-4">
          <FiltroPeriodo de={filtros.de} ate={filtros.ate} onAlterar={(periodo) => definir(periodo)} />
          <Campo rotulo="Cruzar com" className="w-full sm:w-56">
            {(atributos) => (
              <Selecao
                {...atributos}
                value={dimensao}
                opcoes={DIMENSOES_AGRUPAMENTO.map((item) => ({
                  valor: item,
                  rotulo: ROTULO_DIMENSAO[item],
                }))}
                onChange={(evento) => definir({ agruparPor: evento.target.value })}
              />
            )}
          </Campo>
        </div>
      </Cartao>

      {consulta.isError ? (
        <EstadoDeErro erro={consulta.error} aoTentarNovamente={() => void consulta.refetch()} />
      ) : (
        <div className="flex flex-col gap-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <CartaoIndicador
              rotulo="Total de perdas no período"
              tom="perigo"
              valor={formatarNumero(dados?.total ?? 0)}
              carregando={consulta.isPending}
            />
            <CartaoIndicador
              rotulo="Motivo mais frequente"
              valor={dados?.motivos[0]?.rotulo ?? '—'}
              descricao={
                dados?.motivos[0]
                  ? `${formatarNumero(dados.motivos[0].total)} leads · ${formatarPercentual(
                      dados.motivos[0].percentual,
                    )}`
                  : undefined
              }
              carregando={consulta.isPending}
            />
          </div>

          <Cartao semPadding>
            <CabecalhoCartao
              titulo="Motivos"
              descricao="Motivo desativado depois do registro continua aparecendo com o próprio título."
            />
            <CorpoCartao>
              {consulta.isPending ? (
                <div className="space-y-3">
                  {Array.from({ length: 6 }, (_, indice) => (
                    <Esqueleto key={indice} className="h-8 w-full" />
                  ))}
                </div>
              ) : (
                <BarrasHorizontais
                  linhas={(dados?.motivos ?? []).map((motivo) => ({
                    rotulo: motivo.rotulo,
                    valor: motivo.total,
                    anotacao: formatarPercentual(motivo.percentual),
                    cor: 'var(--estado-critico)',
                    onSelecionar: () => navegar(urlDoDrilldown(motivo.drilldown)),
                  }))}
                  vazio="Nenhuma perda registrada no período."
                />
              )}
            </CorpoCartao>
          </Cartao>

          {dimensao !== 'motivo' && (
            <Cartao semPadding>
              <CabecalhoCartao
                titulo={`Concentração por ${ROTULO_DIMENSAO[dimensao].toLowerCase()}`}
                descricao="Onde cada motivo se concentra — costuma ser decisão de mídia, não de atendimento."
              />
              <CorpoCartao>
                {consulta.isPending ? (
                  <Esqueleto className="h-40 w-full" />
                ) : (dados?.cruzamento.length ?? 0) === 0 ? (
                  <EstadoVazio titulo="Sem cruzamento no período" />
                ) : (
                  <ul className="flex flex-col gap-4">
                    {dados?.cruzamento.map((linha) => (
                      <li key={String(linha.id ?? linha.rotulo)}>
                        <div className="mb-2 flex items-baseline justify-between gap-3">
                          <span className="font-medium text-conteudo">{linha.rotulo}</span>
                          <span className="text-xs tabular-nums text-conteudo-fraco">
                            {formatarNumero(linha.total)} perdas
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {linha.porMotivo.map((motivo) => (
                            <Etiqueta key={motivo.id} tom="perigo">
                              {motivo.rotulo}: {motivo.total}
                            </Etiqueta>
                          ))}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CorpoCartao>
            </Cartao>
          )}

          {dimensao === 'motivo' && (
            <Alerta tom="info">
              Com o agrupamento em <strong>Motivo de perda</strong>, o cruzamento sai vazio por
              definição — cruzar motivo com motivo não acrescenta informação. Escolha outra dimensão
              para ver onde a perda se concentra.
            </Alerta>
          )}
        </div>
      )}
    </>
  )
}
